import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { BlogPost, BlogPostsResponse } from '@/types/blog';

interface UseBlogPostsParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  published?: boolean;
  featured?: boolean;
}

// Fetch all blog posts
export function useBlogPosts(params: UseBlogPostsParams = {}) {
  const { page = 1, limit = 10, search = '', category, published, featured } = params;

  return useQuery<BlogPostsResponse>({
    queryKey: ['blog', page, limit, search, category, published, featured],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(category && { category }),
        ...(published !== undefined && { published: published.toString() }),
        ...(featured !== undefined && { featured: featured.toString() }),
      });

      const response = await axios.get(`/api/blog?${queryParams}`);
      return response.data;
    },
  });
}

// Fetch single blog post by slug
export function useBlogPost(slug: string) {
  return useQuery<BlogPost>({
    queryKey: ['blogPost', slug],
    queryFn: async () => {
      const response = await axios.get(`/api/blog/${slug}`);
      return response.data;
    },
    enabled: !!slug,
  });
}

// Create blog post
export function useCreateBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<BlogPost>) => {
      const response = await axios.post('/api/blog', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog'] });
    },
  });
}

// Update blog post
export function useUpdateBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: Partial<BlogPost> }) => {
      const response = await axios.put(`/api/blog/${slug}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['blogPost', variables.slug] });
      queryClient.invalidateQueries({ queryKey: ['blog'] });
    },
  });
}

// Delete blog post
export function useDeleteBlogPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await axios.delete(`/api/blog/${slug}`);
      return response.data;
    },
    onSuccess: (_, slug) => {
      queryClient.invalidateQueries({ queryKey: ['blogPost', slug] });
      queryClient.invalidateQueries({ queryKey: ['blog'] });
    },
  });
}
